import { defineStore } from 'pinia'
import ApiTeam from '@/apis/team'
import { IApiTeam } from '@/apis/team/type.ts'
import { TEAM_CURT_INFO } from '@/global/constants.ts'
import { sessionCache } from '@/utils/comm'

interface ITeamState {
  teamList: any[]
  teamCurtRoleByUser: string
  teamCurtInfo: {
    [key: string | number]: any
    id: number | string
    avatar: string
    config: any
    createTime: string | number
    name: string
    paymentType: string
    type: string
    companyAvatar: string
    companyName: string
  }
  teamCurtStatistics: {
    [key: string | number]: any
    member: string | number
    project: string | number
    bloggers: string | number
  }
  teamMemberList: any[]
  teamMemberListAll: any[]
  teamMemberTotal: 0
  teamMemberPagination: {
    pageNo: number
    pageSize: number
  }
}

const useTeamStore = defineStore('team', {
  state: (): ITeamState => ({
    teamList: [],
    teamCurtRoleByUser: '',
    teamCurtInfo: sessionCache.getCache(TEAM_CURT_INFO) ?? {
      id: '',
      avatar: '',
      config: '',
      createTime: '',
      name: '',
      paymentType: '',
      type: '',
      companyAvatar: '',
      companyName: ''
    },
    teamCurtStatistics: {
      member: 0,
      project: 0,
      bloggers: 0
    },
    teamMemberList: [],
    teamMemberListAll: [],
    teamMemberTotal: 0,
    teamMemberPagination: {
      pageNo: 1,
      pageSize: 10
    }
  }),
  actions: {
    // 团队 列表
    async actionTeamList() {
      const res = await ApiTeam.list()
      this.teamList = res?.data ?? []

      // 设置初始值
      const team_curt_info = sessionCache.getCache(TEAM_CURT_INFO)
      if (!team_curt_info && res?.data?.[0]) {
        await this.actionChangeTeam(res?.data?.[0])
      } else {
        if (this.teamCurtInfo.id) {
          await this.actionTeamRole()
          await this.actionTeamStatistics()
          await this.actionTeamMemberList()
          await this.actionTeamMemberListAll()
          await this.actionTeamSystemInfo()
        }
      }
    },

    async actionChangeTeam(obj: any) {
      Object.keys(this.teamCurtInfo).forEach(key => {
        this.teamCurtInfo[key] = obj?.[key] ?? ''
      })
      sessionCache.setCache(TEAM_CURT_INFO, this.teamCurtInfo)

      if (this.teamCurtInfo.id) {
        await this.actionTeamRole()
        await this.actionTeamStatistics()
        await this.actionTeamMemberList()
        await this.actionTeamMemberListAll()
        await this.actionTeamSystemInfo()
      }
    },

    // 新增
    async actionTeamAdd(params: IApiTeam.TAddOrEdit) {
      await ApiTeam.add(params)
    },

    // 编辑
    async actionTeamEdit(params: IApiTeam.TAddOrEdit) {
      await ApiTeam.edit(params)
    },

    // 删除
    async actionTeamDel(params: IApiTeam.TDel) {
      await ApiTeam.del(params)
      if (params.groupId == this.teamCurtInfo.id) {
        this.teamCurtInfo.id = ''
      }
    },

    // 登录者在团队中（角色）
    async actionTeamRole() {
      const res = await ApiTeam.role({ groupId: this.teamCurtInfo.id })
      this.teamCurtRoleByUser = res?.data ?? ''
    },

    // 统计
    async actionTeamStatistics() {
      const params = { groupId: this.teamCurtInfo.id }
      const res = await ApiTeam.statistics(params)
      Object.keys(this.teamCurtStatistics).forEach(key => {
        this.teamCurtStatistics[key] = res?.data?.[key] ?? ''
      })
    },

    // 成员 list
    async actionTeamMemberList(paginationParams?: { pageNo?: number; pageSize?: number }) {
      // 如果传入分页参数，更新状态
      if (paginationParams) {
        if (paginationParams.pageNo) {
          this.teamMemberPagination.pageNo = paginationParams.pageNo
        }
        if (paginationParams.pageSize) {
          this.teamMemberPagination.pageSize = paginationParams.pageSize
          // 如果改变每页条数，重置到第一页
          this.teamMemberPagination.pageNo = 1
        }
      }
      
      const params = { 
        groupId: this.teamCurtInfo.id, 
        pageNo: this.teamMemberPagination.pageNo, 
        pageSize: this.teamMemberPagination.pageSize 
      }
      const res = await ApiTeam.memberList(params)
      this.teamMemberList = res.data.list
      this.teamMemberTotal = res.data.total
    },

    // 获取全部团队成员（用于下拉选择器等场景）
    async actionTeamMemberListAll() {
      try {
        const res = await ApiTeam.memberListAll({ groupId: this.teamCurtInfo.id })
        this.teamMemberListAll = res?.data ?? []
      } catch (error) {
        console.error('获取全部团队成员失败:', error)
        this.teamMemberListAll = []
      }
    },

    //   团队 系统logo 跟 名称

    async actionTeamSystemInfo() {
      const params = { groupId: this.teamCurtInfo.id }
      const res = await ApiTeam.detailSystemInfo(params)
      this.teamCurtInfo.companyAvatar = res?.data?.companyAvatar ?? ''
      this.teamCurtInfo.companyName = res?.data?.companyName ?? ''
      sessionCache.setCache(TEAM_CURT_INFO, this.teamCurtInfo)
    },
    //   团队 系统logo 跟 名称
    async actionChangeTeamSystemInfo(params: IApiTeam.TSystemInfoAddOrEdit) {
      try {
        await ApiTeam.addOrEditSystemInfo(params)
        this.teamCurtInfo.companyAvatar = params?.companyAvatar ?? ''
        this.teamCurtInfo.companyName = params?.companyName ?? ''
        sessionCache.setCache(TEAM_CURT_INFO, this.teamCurtInfo)
      } catch {}
    }
  }
})

export default useTeamStore
